import type { DriveStep } from 'driver.js';
import { defineStore } from 'pinia';
import { watch } from 'vue';
import { useModalStore } from '../modalStore';
import { useSidebarStore } from '../sidebarStore';
import { EXERCISE_LIBRARY_SIDE_LIST, useSideListStore } from '../sideListStore';
import { useTourStore } from './tourStore';
import { useExerciseBasketStore } from '../exerciseLibrary/exerciseBasketStore';
import { useExerciseLibraryStore } from '../exerciseLibrary/exerciseLibraryStore';

export const useExerciseLibraryCoreStore = defineStore('exerciseLibraryCoreTour', () => {
  const tourStore = useTourStore();
  const sidebarStore = useSidebarStore();
  const modalStore = useModalStore();
  const sideListStore = useSideListStore();
  const exerciseBasketStore = useExerciseBasketStore();
  const exerciseLibraryStore = useExerciseLibraryStore();

  /**
   * Tracks whether the tour itself opened the filter panel so we know
   * to close it again when leaving the classification steps.
   */
  let tourOpenedFilterPanel = false;

  /**
   * The classification filter buttons only carry their tour ids
   * (#favorite-videos, #self-videos, #office-videos) inside the
   * `exerciseProgramFilter` panel — the inline filter row on the library page
   * renders `<ExerciseClassification>` without them. So the tour always opens
   * that panel for those steps, regardless of viewport, and closes it again
   * once it leaves them.
   *
   * `openModal` rather than `openPeek`: there is no click to take an anchor
   * from here. The Filter button is only in the DOM below the desktop
   * breakpoint, so anchoring on it would silently drop the panel back to a
   * centred dialogue on exactly the widths where the tour still needs it —
   * passing the element when we have one keeps the peek, and the centred
   * dialogue is the honest fallback when we don't.
   */
  const ensureFilterPanelOpenForTour = () => {
    if (modalStore.isModalOpen('exerciseProgramFilter')) return;
    const filterButton = document.querySelector<HTMLElement>('[data-cy="exercise-filter-button"]');
    modalStore.openModal({ type: 'exerciseProgramFilter', anchorEl: filterButton });
    tourOpenedFilterPanel = true;
  };

  const closeFilterPanelIfTourOpened = () => {
    if (tourOpenedFilterPanel && modalStore.isModalOpen('exerciseProgramFilter')) {
      modalStore.closeModal({ type: 'exerciseProgramFilter', action: 'cancel' });
      tourOpenedFilterPanel = false;
    }
  };

  /**
   * The library side list (`GenericSideList`) drops its whole content slot while
   * collapsed — and it collapses itself whenever a cascade column opens — so the
   * add menu, the template rows and the side-list search simply are not in the
   * DOM then. Expand it before driver.js goes looking for those three steps.
   *
   * Returns the delay the caller should wait for the 300ms width transition, so
   * a list that was already open doesn't pay for it.
   */
  const expandSideListForTour = (): number => {
    if (!sideListStore.isCollapsed(EXERCISE_LIBRARY_SIDE_LIST)) {
      return 0;
    }
    sideListStore.setCollapsed(EXERCISE_LIBRARY_SIDE_LIST, false);
    return 350;
  };

  let stopPatientSelectionWatch: () => void = () => {};

  /**
   * Moves on once a patient actually lands in the basket store. Watching the
   * store instead of clicking the select's dropdown keeps the step independent
   * of that panel's DOM, which teleports to <body> and unmounts the moment an
   * option is picked.
   */
  const advanceOnPatientSelection = (nextStepIndex: number) => {
    stopPatientSelectionWatch();
    const startedWith = exerciseBasketStore.user?.id;
    const stop = watch(
      () => exerciseBasketStore.user?.id,
      (patientId) => {
        if (!patientId || patientId === startedWith) {
          return;
        }
        stopPatientSelectionWatch();
        // Let the dropdown finish closing before the stage moves.
        setTimeout(() => tourStore.driverObject?.moveTo(nextStepIndex), 250);
      },
    );
    stopPatientSelectionWatch = () => {
      stop();
      stopPatientSelectionWatch = () => {};
    };
  };

  const exerciseLibraryCoreTourSteps: DriveStep[] = [
    {
      element: '#exercise-search-bar',
      onHighlightStarted: () => {
        exerciseLibraryStore.resetExerciseFilter();
        exerciseLibraryStore.resetExerciseSorting();
      },
      popover: {
        title: 'Suchleiste',
        description:
          'Hier kannst du Übungen nach ihrem Namen suchen. In einer Übungsbibliothek mit 250 Übungen kann dir das Zeit ersparen.',
        side: 'top',
        onNextClick: () => {
          tourStore.setTourToComplete();
          tourStore.driverObject?.moveNext();
        },
      },
    },
    {
      // SearchToolbar renders the add button twice: a compact one for narrow
      // `@container/gt` widths and the `-desktop` variant that is the visible one
      // from @mobileLandscape up. Tours only start from the ≥1024px help page, so
      // target the desktop variant — the compact one is `display:none` there and
      // driver.js would highlight an invisible element.
      element: '#add-exercise-desktop',
      popover: {
        title: 'Eigenes Video hochladen ',
        description:
          'Du kannst beliebig viele eigene Übungsvideos hochladen. Hier gibt es die Möglichkeit deinen Patienten direkt zu filmen oder ein Video für die Bibliothek zu erstellen.',
        side: 'top',
        onNextClick: () => {
          // The classification filters the next steps highlight only exist inside
          // the filter panel, so open it before driver.js looks for them.
          ensureFilterPanelOpenForTour();
          setTimeout(
            () => {
              tourStore.driverObject?.moveNext();
            },
            tourOpenedFilterPanel ? 350 : 0,
          );
        },
      },
    },
    {
      element: '#favorite-videos',
      popover: {
        title: 'Favoriten',
        description: 'Hier findest du deine als “Favoriten” markierte Übungsvideos',
        side: 'top',
        onPrevClick: () => {
          closeFilterPanelIfTourOpened();
          setTimeout(() => {
            tourStore.driverObject?.movePrevious();
          }, 350);
        },
      },
    },
    {
      element: '#self-videos',
      popover: {
        title: 'Eigene Übungen',
        description:
          'Wenn du eigene Übungsvideos erstellst die nur in deinem Account sichtbar sein sollen, dann findest du sie unter diesem Filter wieder.',
        side: 'top',
      },
    },
    {
      element: '#office-videos',
      popover: {
        title: 'Praxisvideos',
        description:
          'Alle eigenen Übungsvideos  die für alle Mitarbeiter in der Praxis sichtbar sein sollen, findest du hier.',
        side: 'top',
        onNextClick: () => {
          closeFilterPanelIfTourOpened();
          // The next three steps live in the library side list, which renders
          // nothing while collapsed.
          expandSideListForTour();
          setTimeout(() => {
            tourStore.driverObject?.moveNext();
          }, 350);
        },
      },
    },
    {
      // "Neuer Ordner" and "Neue Vorlage" are no longer two toolbar buttons —
      // they are the two entries of this one context menu in the library side
      // list, which only exposes a `data-cy` (GenericContextMenu forwards no id).
      element: '[data-cy="library-add-button"]',
      popover: {
        title: 'Ordner & Vorlagen erstellen',
        description:
          'Über dieses Plus legst du neue Ordner und Vorlagen an. Ordner strukturierst du frei nach den Konzepten deiner Praxis, z. B. nach Krankheitsbildern. Einer Vorlage fügst du anschließend passende Übungen hinzu und ordnest sie einem Ordner zu.',
        side: 'right',
        onPrevClick: () => {
          // The previous step (`#office-videos`) lives inside the filter panel,
          // so re-open it before navigating back.
          ensureFilterPanelOpenForTour();
          setTimeout(
            () => {
              tourStore.driverObject?.movePrevious();
            },
            tourOpenedFilterPanel ? 350 : 0,
          );
        },
      },
    },
    {
      element: '#template-list-elem',
      popover: {
        title: 'Vorlage Aktionen',
        description:
          'Du kannst die Vorlage wie auch Übungen über das Icon nun direkt ins Programm legen und an einen Patienten versenden. Über die “3 Punkte” kannst du die Vorlage öffnen, sie umbenennen, sie in einen anderen Ordner verschieben oder sie löschen.',
        side: 'top',
      },
    },
    {
      element: '#side-list-search',
      popover: {
        title: 'Suche',
        description:
          'Suche Ordner & Vorlagen per Namen. Blitzschnelle Zuweisung an deine Patienten garantiert!',
        side: 'top',
      },
    },
    {
      element: '#exercise-package-grid-elem',
      popover: {
        title: 'Trainingsziele',
        description: 'Klicke auf diese Kachel, um über 250 Übungen zu finden.',
        side: 'top',
        nextBtnText: `<span class="opacity-50">Weiter →</span>`,
        onPopoverRender: async () => {
          tourStore.useListenToClick('#exercise-package-grid-elem', tourStore.driverObject, 9);
        },
        onNextClick: () => {},
        onPrevClick: () => {
          // Going back re-enters the side-list steps, and the basket column that
          // opens later in the tour collapses the list behind us.
          const delay = expandSideListForTour();
          setTimeout(() => {
            tourStore.driverObject?.movePrevious();
          }, delay);
        },
      },
    },
    {
      element: '#exercise-grid-elem',
      popover: {
        title: 'Übungsvideo',
        description:
          'Du kannst den Namen, Zielbereiche, Körperregionen und die Dauer einer Wiederholung direkt auf einem Blick sehen. Per Klick erhältst du eine Detailansicht.',
        side: 'left',
        onPrevClick: async () => {
          document.querySelector<HTMLButtonElement>('#exercise-grid-back-button')?.click();
          setTimeout(() => {
            tourStore.driverObject?.movePrevious();
          }, 100);
        },
      },
    },
    {
      element: '#add-to-template-button',
      popover: {
        title: 'Übung einer Vorlage hinzufügen',
        description:
          'Per Klick auf diesen Button, kannst du diese Übung einer bestehenden Vorlage hinzufügen.',
        side: 'top',
      },
    },
    {
      element: '#add-to-program-button',
      popover: {
        title: 'Übung ins Programm legen',
        description: `
        <span class='font-bold'>Klicke auf diesen Button</span>, um die Übung einem Übungsprogramm hinzuzufügen, welches direkt an einen Patienten gesendet werden kann.
        <span
        class="relative tabletPortrait:flex size-3 hidden"
      >
        <span class="absolute top-22 right-2 inline-flex h-full w-full animate-ping rounded-full bg-tm-petrol-base opacity-100" />
      </span>

        `,
        side: 'top',
        onPopoverRender: async () => {
          tourStore.useListenToClick('#add-to-program-button', tourStore.driverObject, 12);
        },
        onNextClick: async () => {},
        nextBtnText: `<span class="opacity-50">Weiter →</span>`,
      },
    },
    {
      // The separate "Senden" bar below the grid is gone — the basket button in
      // the library toolbar both shows the collected exercises and opens the
      // program panel, so it is one step now.
      element: '#exercise-basket-button',
      popover: {
        title: 'Programm',
        description: `
        Hier werden alle Übungen gesammelt, die du als Programm an einen Patienten senden möchtest.
        <span class='font-bold'>Klicke auf Programm</span>, um es zusammenzustellen.
        <span
        class="absolute tabletPortrait:flex size-3 hidden"
        >
        <span
          class="absolute inline-flex h-full w-full animate-ping rounded-full bg-tm-petrol-base opacity-100 right-2 top-18"
        />
      </span>
         `,
        nextBtnText: `<span class="opacity-50">Weiter →</span>`,
        side: 'bottom',
        onPopoverRender: async () => {
          // Custom click listener with delay to allow the basket panel to render
          // before moving to the next step.
          const element = document.querySelector('#exercise-basket-button');
          if (element) {
            element.addEventListener(
              'click',
              async () => {
                await new Promise((resolve) => setTimeout(resolve, 500));
                tourStore.driverObject?.moveNext();
              },
              { once: true },
            );
          }
        },
        onNextClick: async () => {},
      },
    },
    {
      // The basket panel opens in its PROGRAM view: only the collected exercises
      // are here. Patient, Frequenz und Laufzeit follow in the ASSIGNMENT view
      // behind "Weiter", so the tour walks the panel in that same order.
      element: '#exercise-program-list-elem',
      popover: {
        title: 'Übungseinstellungen',
        description:
          'Passe Wiederholungen und Sätze sowie Pausenzeiten für deinen Patienten an. Du kannst ebenfalls Bemerkungen zu den Übungen schreiben.',
        side: 'top',
      },
    },
    {
      element: '#send-program-btn-anchor',
      popover: {
        title: 'Weiter zum Versand',
        description: `
      <span class='font-bold'>Klicke auf "Weiter"</span>, um zum zweiten Schritt zu gelangen. Dort wählst du den Patienten aus und legst die Laufzeit des Programms fest.
        <span
        class="absolute tabletPortrait:flex size-3 hidden"
        >
        <span
          class="absolute inline-flex h-full w-full animate-ping rounded-full bg-tm-petrol-base opacity-100 left-12 top-18"
        />
      </span>
        `,
        side: 'top',
        onPopoverRender: async () => {
          await tourStore.useListenToClick('#send-program-btn', tourStore.driverObject, 15);
        },
        onNextClick: async () => {},
        nextBtnText: `<span class="opacity-50">Weiter →</span>`,
      },
    },
    {
      // One step for the whole picker. Highlighting the opened dropdown as its
      // own step used to be a dead end: the panel teleports to <body>, so it is
      // not inside the highlighted element and driver.js makes it click-through
      // (see the `.driver-active` override in index.css). Advancing is driven by
      // the selection landing in the store rather than by a raw DOM click, and
      // "Weiter" stays enabled so an already-locked patient is no dead end
      // either.
      element: '#patientDropdown',
      onDeselected: () => {
        stopPatientSelectionWatch();
      },
      popover: {
        title: 'Patienten wählen',
        description: `
      <span class='font-bold'>Wähle einen Patienten</span>, an welchen dieses individuelle Programm gesendet werden soll. Du kannst aus allen Patienten deiner Patientenliste wählen.
        `,
        side: 'top',
        onPopoverRender: () => {
          advanceOnPatientSelection(16);
        },
        onPrevClick: async () => {
          // The picker only exists in the panel's ASSIGNMENT view, so going back
          // has to return the panel to its PROGRAM view first. The panel's own
          // "Zurück" is a GenericSidebarTemplate footer button without an id —
          // drive it through the store instead of clicking the DOM node.
          sidebarStore.backTrigger.next('basket');
          setTimeout(() => {
            tourStore.driverObject?.movePrevious();
          }, 100);
        },
      },
    },
    {
      element: '#patient-invite-button',
      popover: {
        title: 'Patienten einladen',
        description: 'Sollte der Patient noch nicht angemeldet sein, kannst du ihn hier einladen.',
        side: 'top',
      },
    },
    {
      element: '#exercise-frequency-settings',
      popover: {
        title: 'Workoutfrequenz einstellen',
        description: 'Gib an, an welchen Tagen der Patient seine Übungen durchführen soll.',
        side: 'top',
        onPopoverRender: async () => {
          await tourStore.useListenToClick(
            '#exercise-frequency-settings',
            tourStore.driverObject,
            18,
          );
        },
        onNextClick: () => {},
        // No custom `onPrevClick`: the previous step (`#patient-invite-button`)
        // now lives in the same ASSIGNMENT view, so the panel must stay put.
        nextBtnText: `<span class="opacity-50">Weiter →</span>`,
      },
    },
    {
      element: '[data-driverjs-id="date-picker"]',
      popover: {
        title: 'Dauer einstellen',
        description:
          'Hier gibst du die Laufzeit des Programms an, bevor es ausläuft. Du kannst mehrere Programme an einen Patienten aufeinander aufbauen senden.',
        side: 'top',
      },
    },
    {
      element: '#send-program-btn-anchor',
      popover: {
        title: 'Programm absenden',
        description:
          'Somit schickst du das Übungsprogramm an deinen Patienten. Er kann sich über seinen Account einloggen und die Übungen zu Hause abrufen.',
        side: 'top',
        onPopoverRender: () => {
          const sendBtn = document.querySelector('#send-program-btn');
          if (sendBtn) {
            sendBtn.addEventListener(
              'click',
              () => {
                tourStore.driverObject?.destroy();
              },
              { once: true },
            );
          }
        },
        onNextClick: () => {
          tourStore.driverObject?.destroy();
        },
      },
    },
  ] as const;

  return {
    exerciseLibraryCoreTourSteps,
  };
});
